import Fuse from 'fuse.js';

import { fg } from '@atlaskit/platform-feature-flags';
import type { ExternalUser } from '@atlaskit/user-picker';

import { type SlackService, slackServiceFactory, type SlackUser } from '../index';

import { Integration, type IntegrationsResponse, ScopeState, type SlackWorkspace } from './types';

import { fetchExistingIntegrations, fetchExistingIntegrations_deprecated, search } from './index';

export const MAX_USERS = 10;
export const CONNECTED_SLACK_WORKSPACE_KEY_PREFIX = 'infi-slack-workspace-';
export const PLATFORM_SLACK_PRODUCT_KEY = 'platform';
export const SLACK_SOURCE = 'slack';
export const GOOGLE_SERVICE: Integration.GOOGLE_SERVICE = Integration.GOOGLE_SERVICE;
export const MICROSOFT_SERVICE: Integration.MICROSOFT_SERVICE = Integration.MICROSOFT_SERVICE;
export const PLATFORM_SLACK_SERVICE: Integration.PLATFORM_SLACK_SERVICE =
	Integration.PLATFORM_SLACK_SERVICE;

export const getLocalStorageKey = (key: string): string | null => {
	try {
		return localStorage.getItem(key);
		// eslint-disable-next-line no-unused-vars
	} catch (e: unknown) {
		return null;
	}
};

export const getConnectedSlackWorkSpace = (): string | null =>
	getLocalStorageKey(`${CONNECTED_SLACK_WORKSPACE_KEY_PREFIX}${PLATFORM_SLACK_PRODUCT_KEY}`);

export const getExistingIntegrations = async (
	cloudId: string | undefined,
): Promise<IntegrationsResponse> => {
	if (!cloudId) {
		return {} as IntegrationsResponse;
	}

	const integrationsResponse = await (fg('puma-550-remove-cloud-id')
		? fetchExistingIntegrations()
		: fetchExistingIntegrations_deprecated(cloudId));

	if (!integrationsResponse) {
		return {} as IntegrationsResponse;
	}

	return integrationsResponse;
};

export const fuseFactory = <T>(items: T[]): Fuse<T> => {
	return new Fuse(items, {
		keys: ['displayName', 'name', 'email', 'publicName'],
		threshold: 0.2,
	});
};

export const deleteFalsyValues = <T extends object>(obj: T): T => {
	(Object.keys(obj) as Array<keyof T>).filter((k) => !obj[k]).forEach((k) => delete obj[k]);
	return obj;
};

export const mergeExternalUsers = (userLists: ExternalUser[][]): ExternalUser[] => {
	const usersById = userLists
		.reduce((flatUsersList: ExternalUser[], users: ExternalUser[]) => {
			flatUsersList.push(...users);
			return flatUsersList;
		}, [])
		.reduce<{
			[userId: string]: ExternalUser[];
		}>((result, currentValue) => {
			const key = currentValue.email || currentValue.id;
			if (!result[key]) {
				result[key] = [];
			}
			result[key].push(currentValue);
			return result;
		}, {});

	return Object.keys(usersById).map((userId) => {
		return usersById[userId].reduce((result: ExternalUser, currentValue: ExternalUser) => {
			const sources = [...new Set([...result.sources, ...currentValue.sources])];
			const mergedUser = {
				...deleteFalsyValues(result),
				...deleteFalsyValues(currentValue),
			};
			mergedUser.sources = sources;
			return mergedUser;
		});
	});
};

export const slackToExternalUser = (slackUser: SlackUser): ExternalUser => ({
	id: slackUser.id,
	email: slackUser.email,
	name: slackUser.name,
	publicName: slackUser.displayName,
	avatarUrl: slackUser.avatarUrl,
	sources: [SLACK_SOURCE],
	type: 'external_user',
	isExternal: true,
});

export const initSlack = async (
	cloudId: string | undefined,
	createAnalyticsEvent: any,
	existingIntegrations: IntegrationsResponse,
): Promise<{
	selectedWorkspace: SlackWorkspace | null;
	slackService: SlackService | undefined;
	workspaces: SlackWorkspace[];
}> => {
	if (!cloudId) {
		return {
			slackService: undefined,
			workspaces: [],
			selectedWorkspace: null,
		};
	}

	const productSlackService = slackServiceFactory(cloudId, 'platform', createAnalyticsEvent);

	if (!existingIntegrations) {
		return {
			slackService: productSlackService,
			workspaces: [],
			selectedWorkspace: null,
		};
	}

	const workspaces =
		existingIntegrations?.integrationMetadata?.[Integration.PLATFORM_SLACK_SERVICE]?.teams?.filter(
			(workspace) => workspace.scopeState !== ScopeState.NO_SCOPE,
		) || [];

	const slackWorkspaceId = getConnectedSlackWorkSpace();

	let selectedWorkspace: SlackWorkspace | null = null;
	if (workspaces.length > 0 && slackWorkspaceId) {
		selectedWorkspace =
			workspaces.find((slackWorkspace) => slackWorkspace.id === slackWorkspaceId) || null;
	}

	return {
		slackService: productSlackService,
		workspaces,
		selectedWorkspace,
	};
};

export const queryThirdParty = async (
	searchText: string | undefined,
	cloudId: string | undefined,
	connectedIntegrations: Set<Integration>,
	slackUserSearcher: React.MutableRefObject<Fuse<ExternalUser>>,
): Promise<ExternalUser[]> => {
	if (
		!cloudId ||
		!searchText ||
		searchText.trim().length === 0 ||
		connectedIntegrations.size === 0
	) {
		return [];
	}

	const isNonSlackIntegrationConnected =
		connectedIntegrations.has(GOOGLE_SERVICE) || connectedIntegrations.has(MICROSOFT_SERVICE);

	let users = isNonSlackIntegrationConnected
		? await search(searchText, cloudId).catch(() => {
				return [];
			})
		: [];

	if (slackUserSearcher.current) {
		const slackUsers: ExternalUser[] = slackUserSearcher.current
			.search(searchText)
			.map((result) => result.item);

		users = mergeExternalUsers([
			users,
			slackUsers.slice(0, Math.min(slackUsers.length, MAX_USERS)),
		]);
	}

	const fuse = fuseFactory(users);
	const searchResult: ExternalUser[] = fuse.search(searchText).map((result) => result.item);

	return searchResult.slice(0, Math.min(searchResult.length, MAX_USERS));
};

export const createThirdPartyResolver = (
	cloudId: string | undefined,
	connectedIntegrations: Set<Integration>,
	slackUserSearcher: React.MutableRefObject<Fuse<ExternalUser>>,
) => {
	return async (query: string): Promise<ExternalUser[]> => {
		if ((query ?? '').trim().length === 0) {
			return [];
		}

		const thirdPartyContacts = await queryThirdParty(
			query,
			cloudId,
			connectedIntegrations,
			slackUserSearcher,
		);

		return thirdPartyContacts;
	};
};
